/**
 * Serve Supabase Storage images at the size they're actually displayed.
 *
 * WHY THIS EXISTS: profile pictures are uploaded at full camera resolution and served
 * raw. A real one measured on prod is **2,224,135 bytes of PNG** — rendered into a
 * 32px avatar. A sales-wins channel showing 20 cards pulled ~44 MB of images to paint
 * a few hundred pixels.
 *
 * Supabase's render endpoint fixes this and is already enabled on the project. Measured
 * against that same 2.2 MB source:
 *
 *   width=80   ->  1,032 bytes   (avatar @2x)
 *   width=96   ->  1,260 bytes   (card photo @2x)
 *   width=192  ->  2,846 bytes
 *   width=400  ->  6,600 bytes   (background watermark)
 *
 * i.e. ~1,700x smaller at avatar size.
 *
 * FORMAT: no `format` param is passed on purpose. The endpoint content-negotiates from
 * the browser's Accept header and returns **WebP** automatically (verified: sending
 * `Accept: image/avif,image/webp` returns `image/webp`). Forcing a format would break
 * that negotiation and pin every browser to one codec — including the older ones that
 * still need PNG/JPEG.
 *
 * Non-Supabase and already-transformed URLs pass through untouched, so this is safe to
 * wrap around any src.
 */
// This deployment's own Supabase host. Plain NEXT_PUBLIC_SUPABASE_URL is unset in
// this app — the URL ships obfuscated as NEXT_PUBLIC_SB_U (reversed base64, same
// decode supabaseClient/noteHtml use). Cached; null when neither var resolves.
let sbHostCache: string | null | undefined;
export function supabaseHost(): string | null {
  if (sbHostCache !== undefined) return sbHostCache;
  try {
    let url = process.env.NEXT_PUBLIC_SUPABASE_URL || "";
    if (!url) {
      const ob = (process.env.NEXT_PUBLIC_SB_U || "").replace(/^["']|["']$/g, "");
      if (ob) url = atob(ob.split("").reverse().join(""));
    }
    sbHostCache = url ? new URL(url).hostname : null;
  } catch {
    sbHostCache = null;
  }
  return sbHostCache;
}

export function transformedImage(
  url: string | null | undefined,
  opts: { width: number; height?: number; quality?: number; resize?: "cover" | "contain" } = { width: 96 },
): string | undefined {
  if (!url) return undefined;

  // Only Supabase Storage public objects can be transformed. Anything else (gravatar,
  // an external CDN, a data: URI) is returned as-is rather than silently 404'ing.
  if (!url.includes("/storage/v1/object/public/")) return url;

  // Already pointed at the render endpoint — don't double-rewrite.
  if (url.includes("/render/image/")) return url;

  const base = url.replace("/storage/v1/object/public/", "/storage/v1/render/image/public/");
  const params = new URLSearchParams({
    width: String(opts.width),
    height: String(opts.height ?? opts.width),
    // "cover" square-crops — right for face avatars, WRONG for wide agency logos
    // (a 4267×3000 logo became a center slice). Logo call sites pass "contain",
    // which fits the whole image in the box at its true aspect ratio.
    resize: opts.resize ?? "cover",
    quality: String(opts.quality ?? 70),
  });
  return `${base}?${params.toString()}`;
}
